import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useRealtime } from '../../contexts/RealtimeContext';
import { tuitionTeacherService } from '../../firebase/tuition';
import { motion } from 'framer-motion';
import { Lock, UserCheck, Clock, XCircle } from 'lucide-react';

const TeacherProtectedRoute = ({ children }) => {
  const { currentUser } = useRealtime();
  const [teacherStatus, setTeacherStatus] = useState({ isRegistered: false, status: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkTeacherStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const status = await tuitionTeacherService.getTeacherRegistrationStatus(currentUser.uid);
        setTeacherStatus(status);
      } catch (error) {
        setTeacherStatus({ isRegistered: false, status: null });
      } finally {
        setLoading(false);
      }
    };

    checkTeacherStatus();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking teacher status...</p>
        </motion.div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!teacherStatus.isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCheck className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Teacher Registration Required
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to register as a teacher to access the Teacher Dashboard. Please complete your teacher registration first.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/tuition-teachers'}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Register as Teacher
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Go Back
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (teacherStatus.status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Pending Approval
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your teacher registration is under review. Please wait for admin approval to access the Teacher Dashboard.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (teacherStatus.status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Registration Rejected
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your teacher registration has been rejected. Please contact support for more information or try registering again.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/tuition-teachers'}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Register Again
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Go Back
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (teacherStatus.status === 'approved') {
    return children;
  }

  // Fallback - redirect to dashboard
  return <Navigate to="/dashboard" replace />;
};

export default TeacherProtectedRoute;
